import React, { useEffect, useMemo, useState } from 'react';

import cx from 'classnames';
import PropTypes from 'prop-types';
import CSSModule from 'react-css-modules';
import { FormattedMessage, injectIntl } from 'react-intl';

import {
  COUPON_EXPIRY_TYPES,
  COUPON_IMAGES_FIELD_NAME_PREFIX,
  COUPON_LIST_DATE_FORMAT_PATTERN,
  COUPON_STATES,
  COUPON_TEMPLATE_DATE_FORMAT_PATTERN,
  COUPON_TYPES,
} from 'coupon-common';
import { parseISOString } from 'coupon-common/src/utils';

import ImageSlider from '../ImageSlider/ImageSlider';
import styles from './Coupon.module.scss';
import {
  BlockedIcon,
  DraftIcon,
  ExpiredIcon,
  HeartBlackIcon,
  InfinityBlackIcon,
  PublishedIcon,
} from './icons';
import messages from './messages.js';

const DEFAULT_COUPON_EXPIRY_VALUE = 'xxx';

// The API sends percentage_off as a decimal (e.g. "20.00"), which reads
// oddly as a deal badge — round it to a plain whole number for display.
const formatPercentageOff = value => Math.round(Number(value) || 0);

/**
 * DESIGN UPDATE: markup/styling ported to match CustomerCouponCard's new
 * card language (rounded-16px card, square 1/1 image with overlay
 * badges/heart circle top-right, red-gradient deal-amount pill, business
 * row + title/footer living in a body BELOW the image instead of as text
 * painted over it). Same tokens/mixins as CustomerCouponCard.module.scss
 * (gradient-bg, no-native-button-chrome, $text-muted: #00000040, etc).
 *
 * This card still owns logic CustomerCouponCard has no equivalent for —
 * template/preview modes, the amount-limit expiry type (vs date-only on
 * the customer card), user_limit chip, and the publish-state status icon
 * (blocked/published/draft/expired) with its tooltip — all preserved as
 * before, just re-skinned onto the new layout.
 *
 * Previously the title + usage info were painted directly over the image
 * (coupon-template-content + image-scrim vignette so the text stayed
 * legible). That overlay is gone: title and pricing/expiry now live in
 * .body below the image, same placement CustomerCouponCard uses for
 * .title/.footer-bottom. is_sponsored now renders as a plain badge in the
 * top-left badges cluster (like CustomerCouponCard's "Sponsored" badge)
 * instead of swapping out the heart icon — the heart-style circle in the
 * top-right is now always the static heart icon.
 */
export const Coupon = ({
  intl,
  data,
  businessData,
  templateMode,
  previewMode,
  squareImages,
  layout,
  actionsNode,
}) => {
  const {
    title,
    type,
    amount_off,
    points_back,
    percentage_off,
    expiry_type,
    expiration_quantity,
    expiration_date,
    expiry,
    published_at,
    state,
    is_sponsored,
    is_blocked,
    user_limit,
  } = data;

  const { logo = '', name = '' } = businessData || {};
  const [couponImages, setCouponImages] = useState([]);
  const [logoFailed, setLogoFailed] = useState(false);

  const isCouponPublished = state === COUPON_STATES.PUBLISHED && !is_blocked;
  const isPreviewMode = !templateMode && (!state || previewMode);

  useEffect(() => {
    let coupon_images = [];
    Object.keys(data)
      .sort()
      .forEach(key => {
        if (key.includes(COUPON_IMAGES_FIELD_NAME_PREFIX) && data[key]) {
          coupon_images.push(data[key]);
        }
      });

    if (coupon_images.length === 0 && data.hasOwnProperty('image_url')) {
      coupon_images = data['image_url']
        .sort((a, b) => a.sequence - b.sequence)
        .map(image => image.image_url);
    }

    setCouponImages(coupon_images);
  }, [data]);

  useEffect(() => {
    setLogoFailed(false);
  }, [logo]);

  // First non-whitespace character of the business name, uppercased — used
  // as a fallback avatar when the business has no logo on file, matching
  // the same treatment CustomerCouponCard uses.
  const businessInitial = ((name || '').trim()[0] || '?').toUpperCase();
  const showLogo = logo && !logoFailed;

  const couponStatusIconContent = useMemo(() => {
    let couponStatusIcon;
    let couponStatusTooltipMsg;
    switch (true) {
      case is_blocked:
        couponStatusIcon = BlockedIcon;
        couponStatusTooltipMsg = messages.blockedIconTooltip;
        break;
      case state === COUPON_STATES.PUBLISHED:
        couponStatusIcon = PublishedIcon;
        couponStatusTooltipMsg = messages.publishedIconTooltip;
        break;
      case state === COUPON_STATES.DRAFT:
        couponStatusIcon = DraftIcon;
        couponStatusTooltipMsg = messages.draftIconTooltip;
        break;
      case state === COUPON_STATES.EXPIRED:
        couponStatusIcon = ExpiredIcon;
        couponStatusTooltipMsg = messages.expiredIconTooltip;
        break;
      default:
        couponStatusIcon = null;
        couponStatusTooltipMsg = null;
    }

    return (
      couponStatusTooltipMsg &&
      couponStatusIcon && (
        <div
          styleName="coupon-status-icon"
          className="d-flex justify-content-end align-items-center flex-shrink-1"
        >
          <img
            src={couponStatusIcon}
            alt={intl.formatMessage(messages.couponStateAlt)}
          />
          <span styleName="tooltip-text">
            <FormattedMessage {...couponStatusTooltipMsg} />
          </span>
        </div>
      )
    );
  }, [intl, is_blocked, state]);

  const couponTypeContent = useMemo(() => {
    let content;
    if (type === COUPON_TYPES.AMOUNT_OFF) {
      content = (
        <FormattedMessage {...messages.amountOffText} values={{ amount_off }} />
      );
    }
    if (type === COUPON_TYPES.POINTS_BACK) {
      content = (
        <FormattedMessage
          {...messages.pointsBackText}
          values={{ points_back }}
        />
      );
    }
    if (type === COUPON_TYPES.PERCENTAGE_OFF) {
      content = (
        <FormattedMessage
          {...messages.percentageOffText}
          values={{ percentage_off: formatPercentageOff(percentage_off) }}
        />
      );
    }
    return content;
  }, [type, amount_off, points_back, percentage_off]);

  const couponExpiryContent = useMemo(() => {
    if (expiry_type === COUPON_EXPIRY_TYPES.AMOUNT_LIMIT) {
      const messageForExpiryTypeValue = templateMode
        ? messages.amountLimitText
        : messages.amountText;
      const templateExpirationQuantity =
        expiration_quantity || DEFAULT_COUPON_EXPIRY_VALUE;
      let expirationQuantity =
        expiry && !templateMode ? expiry : templateExpirationQuantity;
      expirationQuantity =
        isPreviewMode && templateExpirationQuantity
          ? templateExpirationQuantity
          : expirationQuantity;
      return (
        <>
          <span styleName="expiry-text">
            <FormattedMessage {...messageForExpiryTypeValue} />
          </span>
          <span styleName="expiry-value">{expirationQuantity}</span>
        </>
      );
    }
    if (expiry_type === COUPON_EXPIRY_TYPES.DATE) {
      const templateExpirationValue = expiration_date
        ? intl.formatDate(
          parseISOString(expiration_date),
          COUPON_TEMPLATE_DATE_FORMAT_PATTERN,
        )
        : DEFAULT_COUPON_EXPIRY_VALUE;
      let expirationValue =
        expiry && !templateMode
          ? intl.formatDate(
            parseISOString(expiry),
            COUPON_TEMPLATE_DATE_FORMAT_PATTERN,
          )
          : templateExpirationValue;
      expirationValue =
        isPreviewMode && expiration_date
          ? templateExpirationValue
          : expirationValue;
      return (
        <span styleName="expiry-text">
          <FormattedMessage {...messages.expiryTypeUntilText} />
          &nbsp;
          {expirationValue}
        </span>
      );
    }
    return null;
  }, [
    expiry_type,
    templateMode,
    expiration_quantity,
    expiration_date,
    intl,
    expiry,
  ]);

  const couponDateContent = useMemo(() => {
    if (
      state === COUPON_STATES.EXPIRED &&
      expiry_type === COUPON_EXPIRY_TYPES.DATE &&
      expiry
    ) {
      const formattedExpirationDate = intl.formatDate(
        expiry,
        COUPON_LIST_DATE_FORMAT_PATTERN,
      );
      const expirationDateLabel = intl.formatMessage(
        messages.expirationDateLabel,
      );
      return `${expirationDateLabel} ${formattedExpirationDate}`;
    }
    if (state === COUPON_STATES.EXPIRED) {
      return intl.formatMessage(messages.couponExpiredText);
    }
    if (published_at) {
      const formattedPublishedAt = intl.formatDate(
        published_at,
        COUPON_LIST_DATE_FORMAT_PATTERN,
      );
      const publidhedAtLabel = intl.formatMessage(messages.publishedDateLabel);
      return `${publidhedAtLabel} ${formattedPublishedAt}`;
    }
    if (state === COUPON_STATES.DRAFT) {
      return intl.formatMessage(messages.draftCouponLabel);
    }
    return intl.formatMessage(messages.publishedDateDefaultText);
  }, [expiry, expiry_type, intl, published_at, state]);

  // ─────────────────────────── LIST LAYOUT ───────────────────────────
  // Horizontal card — image left (square), business row + status icon,
  // title, then a footer row with the deal-amount pill + expiry. Mirrors
  // CustomerCouponCard's layout="list" card. `actionsNode`, when passed,
  // renders at the end of the footer row (used by MyCoupons to slot in
  // its Views/Likes + Edit/Delete controls instead of a Redeem button).
  if (layout === 'list') {
    return (
      <div
        styleName={cx('list-card', {
          'published-coupon-block': isCouponPublished || isPreviewMode,
        })}
      >
        <div styleName="list-image-wrap">
          {couponImages.length > 0 ? (
            <ImageSlider images={couponImages} showNavs showBullets />
          ) : (
            <div styleName="no-image-placeholder" />
          )}

          <div styleName="list-badges-col">
            <span styleName="badge">
              {user_limit && user_limit > 0 ? (
                `x${user_limit}`
              ) : (
                <span styleName="badge-icon-wrap">
                  <img src={InfinityBlackIcon} alt="Infinite" styleName="badge-icon" width={13} height={13} />
                </span>
              )}
            </span>
            {is_sponsored && (
              <FormattedMessage tagName="span" {...messages.sponsoredText}>
                {txt => <span styleName="badge">{txt}</span>}
              </FormattedMessage>
            )}
          </div>

          {/* Publish-state tick/icon moved off the body and onto the
              image itself — top-right corner, same overlay treatment as
              the badges on the left. */}
          {!templateMode && state && (
            <div styleName="list-status-badge">{couponStatusIconContent}</div>
          )}
        </div>

        <div styleName="list-body">
          <div styleName="list-top-row">
            <div styleName="list-header-row">
              {showLogo ? (
                <img
                  styleName="list-logo"
                  src={logo}
                  alt={intl.formatMessage(messages.businessLogoAlt)}
                  onError={() => setLogoFailed(true)}
                />
              ) : (
                <span styleName="list-logo-fallback" aria-label={name || 'Business logo'}>
                  {businessInitial}
                </span>
              )}
              <span styleName="list-business-name">{name}</span>
            </div>

            {/* Deal amount + expiry moved up here (top-right of the body)
                from the old footer row, now that the status icon has
                moved off this row entirely — collapses two rows into
                one, which is what actually shortens the card. */}
            <div styleName="list-deal-inline">
              <span styleName="deal-amount">{couponTypeContent}</span>
              <div styleName="expiry-info">{couponExpiryContent}</div>
            </div>
          </div>

          <p styleName="list-date">{couponDateContent}</p>

          <h3 styleName="list-title">
            {templateMode && !title ? (
              <FormattedMessage {...messages.couponTitleDefaultText} />
            ) : (
              title
            )}
          </h3>

          {/* Now holds only actionsNode (Views/Likes + Edit/Delete from
              MyCoupons) — it's responsible for spreading its own content
              left/right (analytics left, buttons right) across this full
              width row. */}
          {actionsNode && (
            <div styleName="list-footer-row">
              <div styleName="list-actions-slot">{actionsNode}</div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ─────────────────────────── GRID LAYOUT (default) ───────────────────────────
  return (
    <div
      styleName={cx('card', {
        'published-coupon-block': isCouponPublished || isPreviewMode,
      })}
    >
      <div
        styleName="image-wrap"
        style={squareImages ? { aspectRatio: '1 / 1' } : undefined}
      >
        <div styleName="image-container">
          {couponImages.length > 0 ? (
            <ImageSlider images={couponImages} showNavs showBullets />
          ) : (
            <div styleName="no-image-placeholder" />
          )}
        </div>

        <div styleName="badges-row">
          <span styleName="badge">
            {user_limit && user_limit > 0 ? (
              `x${user_limit}`
            ) : (
              <span styleName="badge-icon-wrap">
                  <img src={InfinityBlackIcon} alt="Infinite" styleName="badge-icon" width={13} height={13} />
                </span>
            )}
          </span>
          {is_sponsored && (
            <FormattedMessage tagName="span" {...messages.sponsoredText}>
              {txt => <span styleName="badge">{txt}</span>}
            </FormattedMessage>
          )}
        </div>

      </div>

      <div styleName="body">
        <div styleName="business-header">
          {showLogo ? (
            <img
              styleName="business-logo"
              src={logo}
              alt={intl.formatMessage(messages.businessLogoAlt)}
              onError={() => setLogoFailed(true)}
            />
          ) : (
            <span styleName="business-logo-fallback" aria-label={name || 'Business logo'}>
              {businessInitial}
            </span>
          )}
          <div styleName="business-info">
            <span styleName="business-name">{name}</span>
            <span styleName="business-date">{couponDateContent}</span>
          </div>
          {!templateMode && state && couponStatusIconContent}
        </div>

        <div styleName="footer">
          <h3 styleName="title">
            {templateMode && !title ? (
              <FormattedMessage {...messages.couponTitleDefaultText} />
            ) : (
              title
            )}
          </h3>

          <div styleName="footer-bottom">
            <div styleName="price-group">
              <span styleName="deal-amount">{couponTypeContent}</span>
            </div>
            <div styleName="expiry-info">{couponExpiryContent}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

Coupon.propTypes = {
  intl: PropTypes.object,
  data: PropTypes.object,
  businessData: PropTypes.shape({
    logo: PropTypes.string,
    name: PropTypes.string,
  }).isRequired,
  templateMode: PropTypes.bool,
  previewMode: PropTypes.bool,
  squareImages: PropTypes.bool,
  layout: PropTypes.oneOf(['grid', 'list']),
  actionsNode: PropTypes.node,
};

Coupon.defaultProps = {
  layout: 'grid',
  actionsNode: null,
};

export default injectIntl(CSSModule(Coupon, styles, { allowMultiple: true }));